import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import LoadingSpinner from 'CommonComponents/LoadingSpinner';
import SegmentedControl from 'CommonComponents/SegmentedControl';

import useDropdown from 'Hooks/useDropdown';

import ExclamationTriangleIcon from 'Assets/icons/exclamation-triangle.svg';
import FileDuplicateIcon from 'Assets/icons/file-duplicate.svg';
import XMarkIcon from 'Assets/icons/xmark.svg';

export const SCOPE_FILTER = 'filter';
export const SCOPE_MATCH = 'match';

const CONFLICT_SKIP = 'skip';
const CONFLICT_OVERWRITE = 'overwrite';

/**
 * Applies the value in one allocation cell to every row in a scope.
 *
 * The trigger lives inside the cell it copies from — the cell is the only place
 * that knows which column and which value are in play, so the panel takes both
 * as props rather than re-deriving them.
 *
 * The panel is portalled to <body> so the table's overflow cannot clip it, and
 * anchored by `useDropdown`, the same hook behind the column header's filter and
 * search panels: it pins the panel under the trigger, clamps it to the viewport,
 * closes on an outside click, and closes when the trigger scrolls out of the
 * horizontally scrollable table. A popper tooltip would have needed all of that
 * rebuilt.
 *
 * Portalled only while open, like RowActionsMenu: the trigger now renders on every
 * unallocated row as well, so keeping a hidden panel per trigger would leave
 * hundreds of them parked in <body> on a full page.
 */
export default function BulkApplyPanel({
    column,
    columnLabel,
    value,
    valueLabel,
    sourceRow,
    available,
    filters,
    actualCostType,
    isReconciled,
    urls,
    defaultMatchColumn,
    onApplied,
    toast,
    visible = false,
}) {
    const { triggerRef, dropdownRef, isOpen, close, toggle } = useDropdown();

    const [scope, setScope] = useState(SCOPE_FILTER);
    const [matchColumn, setMatchColumn] = useState(defaultMatchColumn);
    const [conflictMode, setConflictMode] = useState(CONFLICT_SKIP);
    const [counts, setCounts] = useState(null);
    const [loadingFilterCount, setLoadingFilterCount] = useState(false);
    const [loadingMatchCount, setLoadingMatchCount] = useState(false);
    const [progress, setProgress] = useState(null);

    // Each scope's count arrives from its own request, and each can land out of
    // order — a stale response must not overwrite a fresher one.
    const filterRequestIdRef = useRef(0);
    const matchRequestIdRef = useRef(0);

    const matchColumns = [
        { value: 'supplier', label: $t('Supplier'), rowKey: 'supplier_name' },
        { value: 'gl_code', label: $t('G/L Code'), rowKey: 'gl_code' },
        { value: 'document_type', label: $t('Document Type'), rowKey: 'document_type' },
    ];

    const activeMatchColumn = matchColumns.find((option) => option.value === matchColumn) ?? matchColumns[0];
    // No fallback to the supplier: `rowKey` already picks the right field for
    // each column (supplier_name / gl_code / document_type), and a blank one of
    // those is a legitimate match value in its own right (matching on "no G/L
    // code"), not a cue to substitute a different column's value into the label.
    const matchValue = sourceRow?.[activeMatchColumn.rowKey] ?? null;

    // The panel reopens on the value the cell holds now, and the last apply's
    // progress belongs to the last apply.
    useEffect(() => {
        if (isOpen) {
            setScope(SCOPE_FILTER);
            setMatchColumn(defaultMatchColumn);
            setConflictMode(CONFLICT_SKIP);
            setProgress(null);
        }
    }, [isOpen, defaultMatchColumn]);

    const criteria = (forScope) => ({
        type: actualCostType,
        reconciled: isReconciled ? 1 : 0,
        filtersAndSearch: filters,
        column,
        value,
        scope: forScope,
        matchColumn: forScope === SCOPE_MATCH ? matchColumn : null,
        sourceRowId: forScope === SCOPE_MATCH ? sourceRow?.id : null,
        conflictMode,
    });

    // The filter scope is what the panel needs on open — it is the scope shown
    // by default, and actual_costs.event_id/month is indexed, so this is cheap.
    const loadFilterCount = () => {
        const requestId = ++filterRequestIdRef.current;
        setLoadingFilterCount(true);

        axios
            .post(urls.preview, criteria(SCOPE_FILTER))
            .then(({ data }) => {
                if (requestId !== filterRequestIdRef.current) return;
                setCounts((previous) => ({ ...previous, [SCOPE_FILTER]: data }));
            })
            .catch(() => {
                if (requestId !== filterRequestIdRef.current) return;
                toast.error($t('Could not work out how many rows this would change.'));
            })
            .finally(() => {
                if (requestId === filterRequestIdRef.current) setLoadingFilterCount(false);
            });
    };

    // The match scope drops the active filter (see scopeQuery() server-side), so
    // it runs an unindexed scan over supplier / gl_code / document_type across
    // the whole table. Resolved only once the user actually picks "Rows
    // matching" — not on every open, and not on every keystroke elsewhere in the
    // panel — so a page with a large actual_costs table isn't paying for a scan
    // the user may never ask for.
    const loadMatchCount = () => {
        const requestId = ++matchRequestIdRef.current;
        setLoadingMatchCount(true);

        axios
            .post(urls.preview, criteria(SCOPE_MATCH))
            .then(({ data }) => {
                if (requestId !== matchRequestIdRef.current) return;
                setCounts((previous) => ({ ...previous, [SCOPE_MATCH]: data }));
            })
            .catch(() => {
                if (requestId !== matchRequestIdRef.current) return;
                toast.error($t('Could not work out how many rows this would change.'));
            })
            .finally(() => {
                if (requestId === matchRequestIdRef.current) setLoadingMatchCount(false);
            });
    };

    // Deliberately behind refs, and the effects below depend on primitives only:
    // `filters`, `sourceRow` and `toast` are objects whose identity the parent
    // controls, and any of them changing per render would put the count fetch in
    // a loop.
    const loadFilterCountRef = useRef(loadFilterCount);
    loadFilterCountRef.current = loadFilterCount;
    const loadMatchCountRef = useRef(loadMatchCount);
    loadMatchCountRef.current = loadMatchCount;

    useEffect(() => {
        if (isOpen && available) loadFilterCountRef.current();
    }, [isOpen, available, column, value, conflictMode]);

    // `matchColumn` only ever changes together with `scope` becoming SCOPE_MATCH
    // (the selector's own onChange sets both), so gating on `scope` here is what
    // makes this lazy: it fires when the match radio (or the selector, which
    // selects it too) is actually chosen, and again on conflictMode, since
    // invalidCount genuinely varies with it and cannot be derived client-side —
    // but never merely because the panel opened or another field changed.
    useEffect(() => {
        if (isOpen && available && scope === SCOPE_MATCH) loadMatchCountRef.current();
    }, [isOpen, available, scope, column, value, matchColumn, conflictMode]);

    const loadingCounts = scope === SCOPE_FILTER ? loadingFilterCount : loadingMatchCount;
    const active = counts?.[scope] ?? null;
    const netCount = active?.netCount ?? 0;
    const conflictCount = active?.conflictCount ?? 0;
    const invalidCount = active?.invalidCount ?? 0;
    const alreadySetCount = active?.alreadySetCount ?? 0;
    const splitRowCount = active?.splitRowCount ?? 0;
    const dependentInvalidCount = active?.dependentInvalidCount ?? 0;
    const applying = progress !== null;

    const apply = () => {
        // Progress counts rows PROCESSED, which is every row in the scope — the
        // skipped ones included. Seeding it with the net count would make the
        // total jump on the first response.
        setProgress({ processed: 0, total: active?.scopeCount ?? 0 });

        let applied = [];
        let skipped = [];
        let reconciledCount = 0;
        let actionId = null;

        // The first call only resolves the target rows and stores the plan, so the
        // loop below is what actually writes — one segment per request, which is
        // what keeps the progress figure real and the grid responsive.
        const drive = (body) =>
            axios.post(urls.apply, body).then(({ data }) => {
                actionId = actionId ?? data.actionId;
                applied = applied.concat(data.applied ?? []);
                skipped = skipped.concat(data.skipped ?? []);
                reconciledCount += data.reconciledCount ?? 0;
                setProgress({ processed: data.processed ?? 0, total: data.total ?? 0 });

                if (data.nextCursor === null || data.nextCursor === undefined) {
                    return { total: data.total ?? 0 };
                }

                return drive({ actionId: data.actionId, cursor: data.nextCursor });
            });

        drive(criteria(scope))
            .then(() => {
                close();
                onApplied({ actionId, column, columnLabel, value, applied, skipped, reconciledCount });
            })
            .catch(() => {
                close();
                toast.error($t('The bulk apply did not finish. Rows already changed have been saved.'));

                // Whatever landed before the failure is still undoable, so the
                // result bar is shown for it rather than swallowed.
                if (actionId) {
                    onApplied({ actionId, column, columnLabel, value, applied, skipped, reconciledCount });
                }
            })
            .finally(() => setProgress(null));
    };

    return (
        <>
            <button
                type="button"
                ref={triggerRef}
                onClick={(event) => {
                    event.stopPropagation();
                    toggle();
                }}
                className={`tw-shrink-0 tw-flex tw-items-center focus:tw-outline-none ${
                    visible || isOpen ? 'tw-visible' : 'tw-invisible group-hover:tw-visible'
                }`}
                title={$t('Apply this value to other rows')}
                aria-label={$t('Apply this value to other rows')}
                aria-expanded={isOpen}
            >
                <FileDuplicateIcon className="tw-h-3 tw-w-3 tw-text-blue" />
            </button>

            {isOpen &&
                createPortal(
                    <div
                        className="table__filter-modal"
                        ref={dropdownRef}
                        style={{ zIndex: 1000 }}
                        onClick={(event) => event.stopPropagation()}
                        role="dialog"
                        aria-modal="false"
                        aria-label={$t('Apply {{column}}', { column: columnLabel })}
                    >
                        {/* The icon is aria-hidden, so without a label this button announces
                            itself as nothing at all. */}
                        <button type="button" className="table__filter-modal__close-btn" onClick={close} aria-label={$t('Close')}>
                            <span aria-hidden="true">
                                <XMarkIcon className="tw-w-2.5 tw-h-2.5" />
                            </span>
                        </button>

                        <div className="tw-p-4 tw-w-80">
                            <div className="table__filter-modal__title tw-mb-1 tw-pe-4">{$t('Apply {{column}}', { column: columnLabel })}</div>
                            <div className="tw-text-xs tw-font-semibold tw-text-charcoal tw-mb-3 tw-break-words">{valueLabel || $t('Empty')}</div>

                            {!available && <div className="tw-text-xs tw-text-charcoal-75">{$t('Filter the list to apply a value to multiple rows.')}</div>}

                            {!!available && (
                                <>
                                    <div className="tw-text-2xs tw-font-extrabold tw-uppercase tw-text-charcoal-75 tw-mb-2">{$t('Apply to')}</div>

                                    <label className="tw-flex tw-items-center tw-gap-x-2 tw-text-xs tw-font-semibold tw-text-charcoal-50 tw-mb-2">
                                        <input
                                            type="radio"
                                            name={`bulk-apply-scope-${column}-${sourceRow?.id}`}
                                            checked={scope === SCOPE_FILTER}
                                            disabled={applying}
                                            onChange={() => setScope(SCOPE_FILTER)}
                                        />
                                        <span className="tw-flex-1">{$t('Rows in current filter')}</span>
                                        <span className="tw-text-charcoal">{loadingFilterCount ? '—' : (counts?.[SCOPE_FILTER]?.scopeCount ?? 0)}</span>
                                    </label>

                                    {/* The selector is a SIBLING of the label, never inside it: a
                                <select> nested in a <label> is a second interactive control
                                the label also targets, which keyboard and screen-reader users
                                hit even though a mouse click can be stopped from propagating.
                                Choosing a column to match on still selects the match scope —
                                that is in the select's own onChange. */}
                                    <div className="tw-flex tw-items-start tw-gap-x-2 tw-text-xs tw-font-semibold tw-text-charcoal-50">
                                        <label className="tw-flex tw-items-center tw-gap-x-2 tw-mb-0 tw-shrink-0 tw-h-6">
                                            <input
                                                type="radio"
                                                name={`bulk-apply-scope-${column}-${sourceRow?.id}`}
                                                checked={scope === SCOPE_MATCH}
                                                disabled={applying}
                                                onChange={() => setScope(SCOPE_MATCH)}
                                            />
                                            {$t('Rows matching')}
                                        </label>
                                        <div className="tw-flex-1 tw-min-w-0">
                                            <select
                                                className="select-input !tw-h-6 !tw-w-auto !tw-px-2 !tw-text-xs"
                                                value={matchColumn}
                                                disabled={applying}
                                                aria-label={$t('Rows matching')}
                                                onChange={(event) => {
                                                    setMatchColumn(event.target.value);
                                                    setScope(SCOPE_MATCH);
                                                }}
                                            >
                                                {matchColumns.map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                            <span className="tw-block tw-mt-1 tw-truncate tw-text-charcoal">{matchValue || $t('Empty')}</span>
                                        </div>
                                        {/* Not yet resolved until the user actually picks this scope — the
                                    match count is an unindexed scan, so it is not worth running
                                    before there is any intent to use it. */}
                                        <span className="tw-mt-1 tw-text-charcoal">
                                            {loadingMatchCount || !counts?.[SCOPE_MATCH] ? '—' : counts[SCOPE_MATCH].scopeCount}
                                        </span>
                                    </div>

                                    {scope === SCOPE_MATCH && (
                                        <div className="tw-mt-2 tw-text-2xs tw-text-charcoal-75">
                                            {$t('This ignores the current filter and may change rows you cannot see.')}
                                        </div>
                                    )}

                                    <div className="tw-border-t tw-border-grey tw-my-3" />

                                    <div className="tw-flex tw-items-center tw-gap-x-3">
                                        {/* "a different value", not "a value": a row already holding exactly
                                    this value loses nothing, so it is not something Overwrite has any
                                    work to do on. Saying "a value" here would read as a lie on a Type
                                    apply, where every allocated row holds one. */}
                                        <span className="tw-flex-1 tw-text-xs tw-text-charcoal-75">
                                            {$t('{{count}} rows have a different value', { count: loadingCounts ? 0 : conflictCount })}
                                        </span>
                                        <SegmentedControl
                                            variant="dark"
                                            // `dark` sizes its options to the container and truncates them; the
                                            // panel is 320px and the count text beside this takes flex-1, so
                                            // without shrink-0 the control is squeezed under its natural width
                                            // and "Overwrite" ellipsises to "Over…". Let the text wrap instead.
                                            className="!tw-w-auto tw-shrink-0"
                                            value={conflictMode}
                                            onChange={setConflictMode}
                                            // A real `disabled`, not a pointer-events wrapper: the wrapper left
                                            // both buttons tabbable and announced as enabled.
                                            disabled={conflictCount === 0 || applying}
                                            options={[
                                                { value: CONFLICT_SKIP, label: $t('Skip') },
                                                { value: CONFLICT_OVERWRITE, label: $t('Overwrite') },
                                            ]}
                                        />
                                    </div>

                                    {dependentInvalidCount > 0 && (
                                        <div className="tw-flex tw-items-start tw-gap-x-2 tw-mt-3">
                                            <ExclamationTriangleIcon className="tw-w-3.5 tw-h-3.5 tw-shrink-0 tw-text-yellow-400" />
                                            <span className="tw-text-2xs tw-text-charcoal-75">
                                                {$t(
                                                    '{{count}} rows will have their budget line cleared, because it belongs to another department or cost centre.',
                                                    {
                                                        count: dependentInvalidCount,
                                                    },
                                                )}
                                            </span>
                                        </div>
                                    )}

                                    {alreadySetCount > 0 && (
                                        <div className="tw-mt-3 tw-text-2xs tw-text-charcoal-75">
                                            {/* The commonest benign case, and the one that would otherwise be
                                        silent: filter by G/L code where the ERP already put this same
                                        department on every row, and the panel would show a greyed toggle
                                        beside a disabled "Apply to 0" with nothing accounting for the
                                        rows. These are not conflicts — they already hold the value. */}
                                            {$t('{{count}} rows already have this value.', { count: alreadySetCount })}
                                        </div>
                                    )}

                                    {splitRowCount > 0 && (
                                        <div className="tw-mt-3 tw-text-2xs tw-text-charcoal-75">
                                            {$t('{{count}} rows are split across several allocations and are never changed.', {
                                                count: splitRowCount,
                                            })}
                                        </div>
                                    )}

                                    {invalidCount > 0 && (
                                        <div className="tw-mt-3 tw-text-2xs tw-text-charcoal-75">
                                            {/* Without this the button can sit disabled at "Apply to 0" with
                                        nothing on screen explaining why — the common case being a
                                        budget line that belongs to a different department from the
                                        rows being targeted. */}
                                            {$t('{{count}} rows cannot take this value and will be skipped.', { count: invalidCount })}
                                        </div>
                                    )}

                                    {!!active?.capped && (
                                        <div className="tw-mt-3 tw-text-2xs tw-text-charcoal-75">
                                            {$t('Only the first {{count}} rows can be changed in one action. Narrow the filter and apply again.', {
                                                count: active.scopeCount,
                                            })}
                                        </div>
                                    )}

                                    <div className="tw-flex tw-items-center tw-justify-end tw-gap-x-2 tw-pt-4">
                                        {applying && (
                                            <span className="tw-me-auto tw-flex tw-items-center tw-gap-x-2 tw-text-2xs tw-text-charcoal-75">
                                                <LoadingSpinner />
                                                {$t('{{processed}} of {{total}} rows', { processed: progress.processed, total: progress.total })}
                                            </span>
                                        )}
                                        <button type="button" className="tw-text-xs" disabled={applying} onClick={close}>
                                            {$t('Cancel')}
                                        </button>
                                        <button
                                            type="button"
                                            className="table__filter-modal__apply-btn !tw-text-xs"
                                            disabled={applying || loadingCounts || netCount === 0}
                                            onClick={apply}
                                        >
                                            {$t('Apply to {{count}}', { count: netCount })}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>,
                    document.body,
                )}
        </>
    );
}
